import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Checkbox, Form, Input, Radio, message } from "antd";
import { useTranslation } from "react-i18next";
import { isReceiptEligible } from "../../toll/donationReceipt";
import { donationStore } from "../../stores";
import PageBox from "../../layout/PageBox";

const DonationReceipt = observer(() => {
  const { t } = useTranslation();
  const { campaignId, donationId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [needsReceipt, setNeedsReceipt] = useState<boolean | undefined>();
  const [anonymous, setAnonymous] = useState(false);
  const record = donationStore.donationMap.get(donationId ?? "");
  const receiptEligible = record ? isReceiptEligible(record.amount) : false;
  // 旧链接也按原捐款金额显示收据资格，不能通过查询参数绕过门槛。
  useEffect(() => {
    if (donationId) donationStore.getDonation(donationId);
  }, [donationId]);
  // 读取旧记录后保留其匿名及收据选择，避免默认值意外撤销已有申请。
  useEffect(() => {
    if (!record) return;
    setAnonymous(record.isAnonymous ?? false);
    setNeedsReceipt(
      record.receiptOption === "e_receipt"
        ? true
        : record.receiptOption === "no_thanks"
          ? false
          : undefined,
    );
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      receiptTitle: record.receiptTitle,
      donorEmail: record.donorEmail,
    });
  }, [record, form]);
  // 旧书签只更新既有记录，保留令牌验证，不能重新创建捐款。
  const submit = async (values: {
    firstName?: string;
    lastName?: string;
    receiptTitle?: string;
    donorEmail?: string;
  }) => {
    const authToken = localStorage.getItem("donation_authToken");
    if (!donationId || !authToken || !record) {
      message.error(t("campaign_donation.receipt_access_required"));
      return;
    }
    // 历史邮寄值不默认转换，须先明确选择当前可用的收据方式。
    if (receiptEligible && needsReceipt === undefined) {
      message.error(t("campaign_donation.please_complete_receipt"));
      return;
    }
    donationStore.resetUpdateDonation();
    await donationStore.updateDonation({
      id: donationId,
      authToken,
      isAnonymous: anonymous,
      receiptOption:
        needsReceipt && receiptEligible ? "e_receipt" : "no_thanks",
      ...(needsReceipt && receiptEligible ? values : {}),
    });
    if (donationStore.updateDonationSuccess) {
      // 舊收據連結同樣帶回該筆捐款，結果頁才能核實真實付款狀態。
      localStorage.setItem(`fa_donation_${donationId}`, authToken);
      navigate(`/donation/${campaignId}/amount/result/?donationId=${donationId}`);
    }
  };
  return (
    <PageBox spinning={donationStore.updateDonationStatus.loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={submit}
        className="p-[24px]"
      >
        <h1 className="text-[24px] mb-[20px]">
          {t("campaign_donation.receipt_option")}
        </h1>
        {receiptEligible ? (
          <Radio.Group
            value={needsReceipt}
            onChange={(e) => setNeedsReceipt(e.target.value)}
          >
            <Radio value={false}>{t("campaign_donation.no_thanks")}</Radio>
            <Radio value={true}>{t("campaign_donation.e_receipt")}</Radio>
          </Radio.Group>
        ) : (
          <p role="status">
            {record
              ? t("campaign_donation.receipt_eligibility")
              : t("campaign_donation.receipt_access_required")}
          </p>
        )}
        <div className="my-[20px]">
          <Checkbox
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          >
            {t("campaign_donation.anonymous_donations")}
          </Checkbox>
        </div>
        <p className="mb-[20px]">{t("campaign_donation.anonymous_note")}</p>
        {needsReceipt &&
          receiptEligible &&
          (
            [
              ["firstName", "first_name", 50],
              ["lastName", "last_name", 50],
              ["receiptTitle", "receipt_title", 200],
              ["donorEmail", "email_address", 100],
            ] as const
          ).map(([name, label, max]) => (
            <Form.Item
              key={name}
              name={name}
              preserve={false}
              label={t(`campaign_donation.${label}`)}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("campaign_donation.please_complete_receipt"),
                },
                ...(name === "donorEmail"
                  ? [
                      {
                        type: "email" as const,
                        message: t("campaign_donation.invalid_email"),
                      },
                    ]
                  : []),
              ]}
            >
              <Input maxLength={max} />
            </Form.Item>
          ))}
        <Button htmlType="submit" type="primary" disabled={!record}>
          {t("donation_anonymous.confirm")}
        </Button>
      </Form>
    </PageBox>
  );
});
export default DonationReceipt;
